import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useWebRTC } from '../../context/WebRTCContext';
import { submitSong, getRounds } from '../../services/firebase/games';
import { getSongQueue, clearSongQueue } from './SongQueuePanel';
import { logError } from '../../utils/logger';
import { CHATTERS_ID } from '../../constants/game';
import PlayerVideoCard, { KickDialog } from '../../components/PlayerVideoCard';
import { getGridCols, getColCount } from '../../utils/videoGrid';

// Parses a whitelist song entry that may be in "Title - Artist" format.
// Defined at module level so it can be used in useState initialisers.
function parseWhitelistSong(str) {
  const dashIdx = str.indexOf(' - ');
  if (dashIdx !== -1) {
    return { title: str.slice(0, dashIdx).trim(), artist: str.slice(dashIdx + 3).trim() };
  }
  return { title: str.trim(), artist: '' };
}

export default function SongSelectionPhase({ game, round, playerAvatars, isHost }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const webrtc = useWebRTC();
  const isChooser = user.uid === round.chooserId;
  const chooserName = game.playerNames?.[round.chooserId] || 'Unknown';
  const rules = game.rules || {};
  const blacklistArtists = rules.blacklistArtists || [];
  const whitelistArtists = rules.whitelistArtists || [];
  const blacklistSongs   = rules.blacklistSongs   || [];
  const whitelistSongs   = rules.whitelistSongs   || [];
  const hasArtistWhitelist = rules.whitelistArtistsEnabled && whitelistArtists.length > 0;
  const hasSongWhitelist   = rules.whitelistSongsEnabled   && whitelistSongs.length > 0;

  // Pre-queued song (read once on mount when this player is the chooser).
  const rawQueue = isChooser ? getSongQueue(game.id, user.uid) : null;
  const initialQueue = rawQueue ?? null;
  const [queueSource, setQueueSource] = useState(initialQueue);

  // Manual mode state — pre-filled from queue if available
  const [songTitle, setSongTitle] = useState(initialQueue?.title ?? '');
  const [songArtist, setSongArtist] = useState(initialQueue?.artist ?? '');
  // Tracks the selected item string when using a whitelist dropdown.
  // Pre-selects from the queue when a song whitelist is active and the queue matches an entry.
  const [selectedWhitelistSong, setSelectedWhitelistSong] = useState(() => {
    if (!initialQueue || !hasSongWhitelist) return '';
    const normStr = (s) => (s || '').toLowerCase().trim();
    return whitelistSongs.find((s) => {
      const p = parseWhitelistSong(s);
      return normStr(p.title) === normStr(initialQueue.title) &&
             normStr(p.artist) === normStr(initialQueue.artist);
    }) ?? '';
  });
  const [submitting, setSubmitting] = useState(false);

  // Kick dialog
  const [kickConfirmUid, setKickConfirmUid] = useState(null);
  const [kicking, setKicking] = useState(false);

  // Track sm breakpoint for grid column count.
  const [isSm, setIsSm] = useState(() => window.innerWidth >= 640);
  useEffect(() => {
    const handler = () => setIsSm(window.innerWidth >= 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Measure the available height for the other-players grid container.
  const gridContainerRef = useRef(null);
  const [gridContainerH, setGridContainerH] = useState(0);
  useEffect(() => {
    const el = gridContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setGridContainerH(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Played songs from previous rounds (for rule enforcement)
  const [playedSongs, setPlayedSongs] = useState([]);


  // WebRTC helpers
  const hasWebRTC = !!webrtc && !game.inPersonMode;
  const chooserUid = round.chooserId;

  // Memoised to stabilise the ResizeObserver useEffect dependency
  const otherPlayerIds = useMemo(
    () => game.playerIds.filter((id) => id !== chooserUid && id !== CHATTERS_ID),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game.playerIds.join(','), chooserUid]
  );

  // Fetch previously played songs for rule enforcement (chooser only)
  useEffect(() => {
    if (!isChooser) return;
    getRounds(game.id)
      .then((rounds) => {
        const songs = rounds
          .filter((r) => r.song && r.id !== round.id)
          .map((r) => r.song);
        setPlayedSongs(songs);
      })
      .catch(() => {
        // Fail open — rules won't block if fetch fails
      });
  }, [game.id, isChooser, round.id]);


  // ── Rule helpers ───────────────────────────────────────────────────────────

  function norm(s) { return (s || '').toLowerCase().trim(); }

  // No-repeat rules (existing)
  function isRepeatArtistBlocked(artist) {
    if (!rules.noRepeatArtist || !artist.trim()) return false;
    return playedSongs.some((s) => norm(s.artist) === norm(artist));
  }
  function isRepeatSongBlocked(title, artist) {
    if (!rules.noRepeatSong || !title.trim()) return false;
    return playedSongs.some(
      (s) => norm(s.title) === norm(title) && norm(s.artist) === norm(artist)
    );
  }

  // Blacklist / whitelist rules (new)
  function isArtistBlacklisted(artist) {
    if (!rules.blacklistArtistsEnabled || !blacklistArtists.length || !artist.trim()) return false;
    return blacklistArtists.some((a) => norm(a) === norm(artist));
  }
  function isArtistNotWhitelisted(artist) {
    if (!hasArtistWhitelist || !artist.trim()) return false;
    return !whitelistArtists.some((a) => norm(a) === norm(artist));
  }
  function isSongBlacklisted(title) {
    if (!rules.blacklistSongsEnabled || !blacklistSongs.length || !title.trim()) return false;
    return blacklistSongs.some((s) => norm(parseWhitelistSong(s).title) === norm(title));
  }
  function isSongNotWhitelisted(title) {
    if (!hasSongWhitelist || !title.trim()) return false;
    return !whitelistSongs.some((s) => norm(parseWhitelistSong(s).title) === norm(title));
  }

  // ── Manual mode ────────────────────────────────────────────────────────────

  const artistRepeatBlocked   = isRepeatArtistBlocked(songArtist);
  const songRepeatBlocked     = isRepeatSongBlocked(songTitle, songArtist);
  const artistBlacklisted     = isArtistBlacklisted(songArtist);
  const artistNotWhitelisted  = isArtistNotWhitelisted(songArtist);
  const songBlacklisted       = isSongBlacklisted(songTitle);
  const songNotWhitelisted    = isSongNotWhitelisted(songTitle);

  // Combined "is anything blocking submission?"
  const manualBlocked = artistRepeatBlocked || songRepeatBlocked
    || artistBlacklisted || artistNotWhitelisted
    || songBlacklisted   || songNotWhitelisted;

  // Whitelist song selection handler
  function handleWhitelistSongSelect(value) {
    setSelectedWhitelistSong(value);
    if (value) {
      const parsed = parseWhitelistSong(value);
      setSongTitle(parsed.title);
      setSongArtist(parsed.artist);
    } else {
      setSongTitle('');
      setSongArtist('');
    }
  }

  function handleDismissQueue() {
    clearSongQueue(game.id, user.uid);
    setQueueSource(null);
    setSongTitle('');
    setSongArtist('');
    setSelectedWhitelistSong('');
  }

  async function handleKick(uid) {
    setKicking(true);
    try {
      await webrtc?.kickPlayer?.(uid);
    } catch (err) {
      logError(err);
    } finally {
      setKicking(false);
      setKickConfirmUid(null);
    }
  }

  async function handleManualSubmit() {
    if (!songTitle.trim() || manualBlocked) return;
    setSubmitting(true);
    clearSongQueue(game.id, user.uid);
    try {
      await submitSong(game.id, round.id, {
        title: songTitle.trim(),
        artist: songArtist.trim(),
      });
    } catch (err) {
      logError(err);
      setSubmitting(false);
    }
  }

  // Compute explicit row/column counts for the other-players grid
  const otherGridCols = getColCount(otherPlayerIds.length, isSm);
  const otherGridRows = Math.max(1, Math.ceil(otherPlayerIds.length / otherGridCols));

  // Compute max-width so each card fits at 16:9 within the available container height.
  // gap-2 = 8px; pb-3 = 12px bottom padding.
  const GAP_PX = 8;
  const PAD_BOTTOM = 12;
  const MIN_CARD_WIDTH = 160;
  const MIN_CARD_HEIGHT = 90;
  const rowH = gridContainerH > 0
    ? Math.max((gridContainerH - PAD_BOTTOM - (otherGridRows - 1) * GAP_PX) / otherGridRows, MIN_CARD_HEIGHT)
    : MIN_CARD_HEIGHT;
  const cardW = Math.max(rowH * (16 / 9), MIN_CARD_WIDTH);
  const otherGridMaxWidth = cardW * otherGridCols + (otherGridCols - 1) * GAP_PX;

  // ── WebRTC card props ──────────────────────────────────────────────────────

  function webrtcCardProps(uid) {
    if (!hasWebRTC) return {};
    const isMe = uid === user.uid;
    return {
      mediaState: webrtc.playerMediaStates?.[uid],
      localCameraStream: isMe ? webrtc.localCameraStream : null,
      remoteStreamsByUid: !isMe ? webrtc.remoteStreamsByUid?.get(uid) : null,
      cameraStreamId: webrtc.playerMediaStates?.[uid]?.cameraStreamId,
      myCameraEnabled: isMe ? webrtc.cameraEnabled : undefined,
      myMicEnabled: isMe ? webrtc.micEnabled : undefined,
      toggleCamera: isMe ? webrtc.toggleCamera : null,
      toggleMic: isMe ? webrtc.toggleMic : null,
      isMuted: !isMe ? (webrtc.localMutes?.[uid] ?? false) : false,
      volume: !isMe ? (webrtc.localVolumes?.[uid] ?? 1) : 1,
      isHidden: !isMe ? (webrtc.localHidden?.[uid] ?? false) : false,
      onMuteToggle: !isMe
        ? () => (webrtc.localMutes?.[uid] ? webrtc.unmuteRemote?.(uid) : webrtc.muteRemote?.(uid))
        : null,
      onVolumeChange: !isMe ? (v) => webrtc.setRemoteVolume?.(uid, v) : null,
      onHideToggle: !isMe
        ? () => (webrtc.localHidden?.[uid] ? webrtc.showRemoteCamera?.(uid) : webrtc.hideRemoteCamera?.(uid))
        : null,
      onKick: isHost && !isMe ? () => setKickConfirmUid(uid) : null,
    };
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">

      {/* ── Chooser + form — flex-none so the grid gets all remaining space ── */}
      <div className="flex-none flex flex-col">

        {/* Chooser camera — height-capped, aspect-ratio preserving */}
        {hasWebRTC && (
          <div className="px-3 pt-3 flex justify-center h-[20vh] sm:h-[30vh]">
            <div className="aspect-video h-full max-w-full shrink-0">
              <PlayerVideoCard
                uid={chooserUid}
                name={chooserName}
                avatarEmoji={playerAvatars?.[chooserUid]}
                isMe={isChooser}
                stretch
                className="w-full h-full"
                {...webrtcCardProps(chooserUid)}
                renderTopLeft={() => (
                  <p className="t-text text-xs font-semibold leading-tight drop-shadow truncate">
                    {chooserName}
                  </p>
                )}
              />
            </div>
          </div>
        )}

        {/* Main form / waiting area */}
        <div className="flex items-center justify-center px-4 py-3">
          <div className="w-full max-w-lg space-y-3">
            <div className="text-center">
              <h2 className="text-xl font-bold t-text">
                {isChooser ? t('songSelection.yourTurn') : t('songSelection.choosingTitle', { name: chooserName })}
              </h2>
            </div>

            {isChooser ? (
              <div className="t-bg-surface t-border border rounded-2xl p-4 space-y-3">

                {queueSource && (
                  <div className="flex items-center justify-between t-bg-accent-muted rounded-lg px-3 py-2">
                    <span className="text-xs t-text-accent font-medium">
                      {t('songQueue.preFilledFromQueue')}
                    </span>
                    <button
                      type="button"
                      onClick={handleDismissQueue}
                      className="text-xs t-text-muted hover:t-text transition-colors t-focus-ring rounded"
                    >
                      {t('songQueue.startFresh')}
                    </button>
                  </div>
                )}

                {/* ── Whitelist songs: single dropdown ── */}
                {hasSongWhitelist ? (
                  <div>
                    <label className="block text-xs font-medium t-text-secondary mb-1">
                      {t('songSelection.selectSongLabel')}
                    </label>
                    <select
                      value={selectedWhitelistSong}
                      onChange={(e) => handleWhitelistSongSelect(e.target.value)}
                      className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-focus-input"
                    >
                      <option value="">{t('songSelection.selectSongPlaceholder')}</option>
                      {whitelistSongs.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  /* ── Free-text title (also shown when only artist whitelist) ── */
                  <input
                    type="text"
                    value={songTitle}
                    onChange={(e) => setSongTitle(e.target.value)}
                    placeholder={t('songSelection.titlePlaceholder')}
                    maxLength={200}
                    className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-placeholder t-focus-input"
                  />
                )}

                {/* ── Artist: whitelist dropdown or free-text (unless song whitelist covers it) ── */}
                {!hasSongWhitelist && (
                  hasArtistWhitelist ? (
                    <div>
                      <label className="block text-xs font-medium t-text-secondary mb-1">
                        {t('songSelection.selectArtistLabel')}
                      </label>
                      <select
                        value={songArtist}
                        onChange={(e) => setSongArtist(e.target.value)}
                        className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-focus-input"
                      >
                        <option value="">{t('songSelection.selectArtistPlaceholder')}</option>
                        {whitelistArtists.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={songArtist}
                      onChange={(e) => setSongArtist(e.target.value)}
                      placeholder={t('songSelection.artistPlaceholder')}
                      maxLength={80}
                      className="w-full t-bg-input t-border-input border rounded-lg px-4 py-2 t-text t-placeholder t-focus-input"
                    />
                  )
                )}

                {/* ── Validation messages ── */}
                {artistRepeatBlocked && (
                  <p role="alert" className="text-xs t-text-error">
                    {t('songSelection.artistAlreadyPlayed', { artist: songArtist })}
                  </p>
                )}
                {!artistRepeatBlocked && songRepeatBlocked && (
                  <p role="alert" className="text-xs t-text-error">
                    {t('songSelection.songAlreadyPlayed')}
                  </p>
                )}
                {artistBlacklisted && (
                  <p role="alert" className="text-xs t-text-error">
                    {t('songSelection.artistBlacklisted', { artist: songArtist })}
                  </p>
                )}
                {!artistBlacklisted && artistNotWhitelisted && songArtist && (
                  <p role="alert" className="text-xs t-text-error">
                    {t('songSelection.artistNotWhitelisted')}
                  </p>
                )}
                {songBlacklisted && (
                  <p role="alert" className="text-xs t-text-error">
                    {t('songSelection.songBlacklisted')}
                  </p>
                )}
                {!songBlacklisted && songNotWhitelisted && songTitle && (
                  <p role="alert" className="text-xs t-text-error">
                    {t('songSelection.songNotWhitelisted')}
                  </p>
                )}

                <button
                  onClick={handleManualSubmit}
                  disabled={submitting || !songTitle.trim() || manualBlocked}
                  className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-2.5 transition-colors t-focus-ring"
                >
                  {submitting ? t('songSelection.startingRound') : t('songSelection.startRound')}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <div
                  className="w-5 h-5 border-2 t-border-accent border-t-transparent rounded-full animate-spin shrink-0"
                  aria-hidden="true"
                />
                <p className="t-text-muted text-sm">{t('songSelection.waitingForChooser', { name: chooserName })}</p>
              </div>
            )}
          </div>
        </div>

      </div>{/* end flex-none */}

      {/* ── Other players grid ────────────────────────────────────────────── */}
      {hasWebRTC && otherPlayerIds.length > 0 && (
        <div ref={gridContainerRef} className="flex-1 min-h-0 px-3 pb-3 overflow-hidden">
          <div
            className={`grid gap-2 mx-auto ${getGridCols(otherPlayerIds.length)}`}
            style={{ maxWidth: `${otherGridMaxWidth}px` }}
          >
            {otherPlayerIds.map((uid) => {
              const pName = game.playerNames?.[uid] || uid;
              return (
                <PlayerVideoCard
                  key={uid}
                  uid={uid}
                  name={pName}
                  avatarEmoji={playerAvatars?.[uid]}
                  isMe={uid === user.uid}
                  {...webrtcCardProps(uid)}
                  renderTopLeft={() => (
                    <p className="t-text text-xs font-semibold leading-tight drop-shadow truncate">
                      {pName}
                    </p>
                  )}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Kick confirmation dialog */}
      {kickConfirmUid && (
        <KickDialog
          name={game.playerNames?.[kickConfirmUid] || kickConfirmUid}
          onConfirm={() => handleKick(kickConfirmUid)}
          onCancel={() => setKickConfirmUid(null)}
          kicking={kicking}
        />
      )}
    </div>
  );
}
